/**
 * Background save submit for useFormState.
 *
 * `saveChanges` applies its event batch optimistically and returns
 * immediately; everything after that — the 409 conflict retry loop,
 * rollback-on-terminal-failure, the failure toast, the Sentry report, and
 * cache invalidation — runs afterward in the background, detached from the
 * triggering render. This module holds that detached task body.
 */
import * as Sentry from '@sentry/react';
import type { QueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import type { CrmObject, OverwriteTarget } from '@/types/crm';

import { MAX_CONFLICT_RETRIES } from '../core/constants';
import type { CRMObjectClient } from '../core/CRMObjectClient';
import {
  getUserFriendlyEventError,
  PropertyTypeConflictError,
  type PropertyTypeError,
} from '../core/errors';
import type { BaseEvent, EntityTypeName } from '../core/types';
import { crmQueryKeys, fetchCrmObjectDetail } from '../query/queries';
import {
  type OptimisticBatchContext,
  rollbackOptimisticBatch,
} from '../store/optimisticBatch';
import { getDisplayName } from '../utils/getDisplayName';
import { getConflictingPropertyKeys } from './conflictDetection';
import { pendingSaveEntityKeys } from './useFormState';

/**
 * Returns true when `error` looks like a seq-ahead HTTP 400 and the one-shot
 * recovery has not yet been used. Exported for pure-logic unit tests.
 *
 * The production loop sets `seqAheadRecoveryUsed = true` immediately after
 * this returns true, so the condition fires at most once per background submit
 * task regardless of how many loop iterations run.
 *
 * Two guards keep this narrow:
 *   1. `error` must be a non-null object before we read `statusCode` — a
 *      non-object throw (string, number, undefined) must not crash the
 *      background handler mid-recovery-check and bypass terminal failure
 *      handling (which would leave the optimistic edit applied).
 *   2. A genuine seq-ahead submit fails as a *plain* HTTP 400 — one with no
 *      structured `error_type` in the response body. Every content/validation
 *      failure the submit endpoint returns (invalid property, required-property
 *      violation, invalid schema reference, …) carries an `error_type` and
 *      cannot be fixed by refetching a fresh seq. Restricting recovery to a
 *      plain 400 means those validation errors surface immediately instead of
 *      incurring a wasteful detail fetch plus a second rejected submit.
 */
export function isSeqAhead400(
  error: unknown,
  seqAheadRecoveryUsed: boolean
): boolean {
  if (seqAheadRecoveryUsed) {
    return false;
  }
  if (typeof error !== 'object' || error === null) {
    return false;
  }
  if ((error as { statusCode?: number }).statusCode !== 400) {
    return false;
  }
  const { response } = error as { response?: unknown };
  const errorType =
    typeof response === 'object' && response !== null
      ? (response as { error_type?: unknown }).error_type
      : undefined;
  // Plain 400 → no structured validation discriminator → seq-ahead candidate.
  return errorType === undefined || errorType === null || errorType === '';
}

/**
 * Everything the background task needs, snapshotted into locals at
 * `saveChanges` call time. This function must never read live hook state —
 * the user may keep editing, or the component may unmount, while the submit
 * is in flight — so every value it touches is passed in explicitly here.
 */
export interface RunBackgroundSaveSubmitParams {
  object: CrmObject;
  entityTypeName: EntityTypeName;
  orgId: number;
  /** The initial event batch, already applied optimistically. */
  events: BaseEvent[];
  /** Rebuilds the event batch against a fresh startSeq for a conflict retry. */
  buildEventsForSeq: (startSeq: number) => BaseEvent[];
  /** The pending property changes' keys, used to detect real vs. incidental conflicts. */
  pendingPropertyChanges: Record<string, unknown>;
  overwriteTargetsValue: OverwriteTarget[];
  baselineProps: Record<string, unknown>;
  optimisticContext: OptimisticBatchContext | null;
  objectDetailKey: ReturnType<typeof crmQueryKeys.objectDetail>;
  client: CRMObjectClient;
  queryClient: QueryClient;
  isMountedRef: { current: boolean };
  setSaveError: (error: string | null) => void;
  onSaveError?: (error: string) => void;
  pendingKey: string;
}

/**
 * Submit + 409 retry + rollback + error toast, run in the background.
 *
 * Everything below is wrapped in a single try/finally so the pending-entity
 * flag clears on every exit path — the success return, every
 * failTerminally() return, and any uncaught throw (including one from
 * failTerminally itself). Do not rely on the retry loop's own returns to
 * clear it; this outer finally is the only place that does.
 */
export async function runBackgroundSaveSubmit({
  object,
  entityTypeName,
  orgId,
  events: initialEvents,
  buildEventsForSeq,
  pendingPropertyChanges,
  overwriteTargetsValue,
  baselineProps,
  optimisticContext,
  objectDetailKey,
  client,
  queryClient,
  isMountedRef,
  setSaveError,
  onSaveError,
  pendingKey,
}: RunBackgroundSaveSubmitParams): Promise<void> {
  let events = initialEvents;
  // One-shot flag: used to guard the seq-ahead-400 recovery so it fires at
  // most once per background submit task (not per retry-loop iteration).
  let seqAheadRecoveryUsed = false;
  try {
    const failTerminally = (error: unknown) => {
      const recordName = getDisplayName(object);
      let message: string;
      if (error instanceof PropertyTypeConflictError) {
        const who = error.lastChangedBy?.name;
        message = who
          ? `${who} just made changes to this record. Please review the latest values and try again.`
          : 'Someone else just made changes to this record. Please review the latest values and try again.';
      } else {
        message = getUserFriendlyEventError(error);
      }
      try {
        if (optimisticContext) {
          rollbackOptimisticBatch(optimisticContext);
        }
      } finally {
        console.error('[useFormState] Background save failed', error);
        Sentry.captureException(error, {
          extra: {
            entityId: object.entity_id,
            entityType: entityTypeName,
            correlationId: (error as PropertyTypeError)?.correlationId,
          },
        });
        // Self-heal: a terminal background-save failure gets the same
        // cache-invalidation refetch the success path performs
        // unconditionally, in case the rollback above didn't fully
        // restore server-truth (e.g. a partial batch, or state that
        // changed underneath the rollback).
        console.warn(
          '[useFormState] Invalidating CRM caches after terminal background save failure',
          { entityId: object.entity_id, entityType: entityTypeName }
        );
        void Promise.all([
          queryClient.invalidateQueries({ queryKey: objectDetailKey }),
          queryClient.invalidateQueries({
            queryKey: crmQueryKeys.objects(orgId, entityTypeName),
            refetchType: 'none',
          }),
          queryClient.invalidateQueries({
            queryKey: ['crm', orgId, 'object', String(object.id)],
            refetchType: 'none',
          }),
          queryClient.invalidateQueries({
            queryKey: ['crm', orgId, 'object', object.entity_id],
            refetchType: 'none',
          }),
        ]);
        toast.error(`Couldn't save changes to ${recordName}`, {
          description: `${message} Your changes were reverted.`,
        });
        if (isMountedRef.current) {
          setSaveError(message);
          onSaveError?.(message);
        }
      }
    };

    for (let attempt = 0; attempt <= MAX_CONFLICT_RETRIES; attempt++) {
      try {
        await client.submitEvents({
          events,
          ...(overwriteTargetsValue.length > 0 && {
            overwrite_targets: overwriteTargetsValue,
          }),
        });

        await Promise.all([
          queryClient.invalidateQueries({ queryKey: objectDetailKey }),
          queryClient.invalidateQueries({
            queryKey: crmQueryKeys.objects(orgId, entityTypeName),
            refetchType: 'none',
          }),
          queryClient.invalidateQueries({
            queryKey: ['crm', orgId, 'object', String(object.id)],
            refetchType: 'none',
          }),
          queryClient.invalidateQueries({
            queryKey: ['crm', orgId, 'object', object.entity_id],
            refetchType: 'none',
          }),
        ]);
        return;
      } catch (error) {
        // Seq-ahead-400 guard: the store seq can be inflated above the
        // committed server seq by an in-flight optimistic inline edit
        // (useUpdateCrmProperty bumps meta.lastSeq pre-commit). A seq-ahead
        // submit fails as a plain HTTP 400, not a PropertyTypeConflictError,
        // so the 409-only conflict retry never catches it. One-shot recovery:
        // fetch server-fresh seq and retry once.
        //
        // Gate on `attempt < MAX_CONFLICT_RETRIES` so recovery only fires when
        // a subsequent loop iteration actually exists to submit the rebuilt
        // batch. On the final iteration, `continue` would rebuild the events
        // and then fall off the bounded loop into "Max retries exceeded"
        // without ever re-submitting — so instead we fall through to terminal
        // handling with the real 400 error, mirroring the conflict-retry path.
        if (
          attempt < MAX_CONFLICT_RETRIES &&
          isSeqAhead400(error, seqAheadRecoveryUsed)
        ) {
          seqAheadRecoveryUsed = true;
          try {
            const freshObject = await fetchCrmObjectDetail(
              queryClient,
              orgId,
              entityTypeName,
              object.entity_id
            );
            events = buildEventsForSeq(freshObject.last_event_seq);
            continue;
          } catch {
            // fetchQuery failed — fall through to terminal handling
          }
        }

        // On seq collision, check for property-level conflicts before retrying
        if (
          error instanceof PropertyTypeConflictError &&
          attempt < MAX_CONFLICT_RETRIES
        ) {
          try {
            const freshObject = await fetchCrmObjectDetail(
              queryClient,
              orgId,
              entityTypeName,
              object.entity_id
            );

            // Check if any of OUR pending properties were changed by someone else
            const conflictingProps = getConflictingPropertyKeys(
              Object.keys(pendingPropertyChanges),
              freshObject.properties ?? {},
              baselineProps
            );

            if (conflictingProps.length === 0) {
              // No property conflicts — a different field changed.
              // Rebuild against the fresh seq and retry.
              events = buildEventsForSeq(freshObject.last_event_seq);
              continue;
            }
            // Real conflict: properties we're editing were changed by
            // someone else — fall through to terminal handling below.
          } catch {
            // Failed to refetch — fall through to terminal handling below.
          }
        }

        failTerminally(error);
        return;
      }
    }

    failTerminally(new Error('Max retries exceeded'));
  } finally {
    pendingSaveEntityKeys.delete(pendingKey);
  }
}
